<template>
  <div class="flex h-12 my-2 justify-between" style='box-sizing:border-box'>
    <slot name='left' />

    <pbc-button @click='showMenu = !showMenu'>
      <menu-icon />
    </pbc-button>

    <pbc-button :disabled='!undoLength' @click="$emit('undo')">
      <undo class="h-8" title="Undo" />
    </pbc-button>
    <pbc-button :disabled='!redoLength' @click="$emit('redo')">
      <redo class="h-8" title="Redo" />
    </pbc-button>

    <pbc-button :disabled='!!editingComponent' @click="add" :class='addClasses'>
      <plus-circle :size='32' title='Add Object' />
    </pbc-button>
    <pbc-button :disabled='!!editingComponent || !hasSelections' @click="startClone">
      <content-duplicate :size='32' title="Duplicate" />
    </pbc-button>
    <pbc-button :disabled='!!editingComponent || !hasSelections' @click="remove">
      <delete class="h-8" :size='32' title="Remove object" />
    </pbc-button>
    <pbc-button @click="onPlay" 
                class="md:grow"
                :disabled='disablePreview'
                :class='highlightPreview ? "pbc-primary-color" : "" '>
      <play class="h-8" :size='32' title="Play" 
             />
    </pbc-button>
    <pbc-popup v-if='showClonePopup' @close='showClonePopup = false'>
      <div class="m-3 rounded shadow flex flex-col justify-center">
        <h3 class="p-1 text-2xl text-gray-800 font-semibold">Instance or Clone?</h3>
        <p class="text-sm tracking-tight">Creating an instance, allows you to edit multiple toys at the same time. Using Clone, you get a brand new toy.</p>
        <div class="p-2 py-4 flex justify-center">
          <pbc-button @click='useClone'>
            <content-duplicate />
            Clone
          </pbc-button>
          <pbc-button @click='useInstance'>
            <link-plus />
            Instance
          </pbc-button>
          <pbc-button @click='showClonePopup = false'>
            <close />
            Cancel
          </pbc-button>
        </div>
        <!--
        <p class="text-xs">You can toggle this at any time by long-pressing the clone/instance button</p>
        -->
      </div>
    </pbc-popup>

    <pbc-popup v-if='showMenu' @close='showMenu = false'>
      <div class="flex flex-col">
        <pbc-button @click="$emit('layers')"><layers />Layers</pbc-button>
        <pbc-button @click="$emit('zoomIn')"><magnify-plus />Zoom In</pbc-button>
        <pbc-button @click="$emit('zoomOut')"><magnify-minus />Zoom Out</pbc-button>
        <pbc-button @click="$emit('settings')"><layers />Settings</pbc-button>
      </div>
    </pbc-popup>

  </div>
</template>
<script>
import PlusCircle from '@icons/PlusCircle.vue';
import ContentDuplicate from '@icons/ContentDuplicate.vue';
import Menu from '@icons/Menu.vue';
import Delete from '@icons/Delete.vue';
import Undo from '@icons/Undo.vue';
import Redo from '@icons/Redo.vue';
import MagnifyMinus from '@icons/MagnifyMinus.vue';
import MagnifyPlus from '@icons/MagnifyPlus.vue';
import Layers from '@icons/Layers.vue';
import Close from '@icons/Close.vue';
import Play from '@icons/Play.vue';
import PbcPopup from '@ui/molecules/PbcPopup.vue';
import LinkPlus from '@icons/LinkPlus.vue';

export default {
  props: {
    // for transform
    selectionsData: {
      type: Array,
      required: true
    },
    // for components
    editingComponent: {
      type: Object,
      default() {
        return null;
      }
    },
    root:{
      type: Object,
      required: true
    },
    undoLength: {
      type: Number,
      default: 0
    },
    redoLength: {
      type: Number,
      default: 0
    },
    primary: {
      type: String,
      default: ''
    },
    noTransformsAdded: {
      type:Boolean,
      default:false
    },
    disablePreview: {
      type:Boolean,
      default:false
    },
  },
  data() {
    return {
      showClonePopup: false,
      highlightPreview: false,
      showMenu: false
    }
  },
  components:{
    PlusCircle,
    ContentDuplicate,
    Delete,
    Undo,
    Redo,
    Play,
    LinkPlus,
    PbcPopup,
    Close,
    MenuIcon: Menu,
    Layers,
    MagnifyPlus,
    MagnifyMinus,
  },
  methods: {
    startClone() {
      this.showClonePopup = true;
    },
    useClone() {
      this.clone();
      this.showClonePopup = false;
    },
    useInstance() {
      this.instance();
      this.showClonePopup = false;
    },
    add() {
      this.$ana.track({
        eventCategory: 'toolbar',
        eventAction: 'add'
      });
      this.addTransforms();
    },
    addTransforms() {
      const activeSelections = this.selectionsData.filter( (sd) => {
        return !sd.indirectlySelected;
      });

      if (activeSelections.length) {
        const parents = activeSelections.map( ({transform}) => {
          return transform;
        });
        const childrenComponents = activeSelections.map( ({childrenComponent}) => {
          return childrenComponent
        });
        this.$emit("add-transforms", {parents,childrenComponents});
      } else {
        this.$emit("add-transforms", {parents:[this.root]});
      }
    },
    getParentsToInstanceOrClone() {
      const activeSelections = this.selectionsData.filter( (sd) => {
        return !sd.indirectlySelected;
      });

      const transformsToClone = activeSelections.map( ({parentTransform, transform}) => {
        return {
          parent: parentTransform,
          child: transform
        }
      });
      return transformsToClone;
    },
    instance() {
      const transformsToClone = this.getParentsToInstanceOrClone();
      this.$emit("instance", {
        transformsToClone
      });
    },
    clone() {
      const transformsToClone = this.getParentsToInstanceOrClone();
      this.$emit("clone", {
        transformsToClone
      });
    },
    remove() {
      const activeSelections = this.selectionsData.filter( (sd) => {
        return !sd.indirectlySelected;
      });

      const transformsToRemove = activeSelections.map( ({parentTransform, transform}) => {
        return {
          parent: parentTransform,
          child: transform
        }
      });

      this.$emit("remove", {
        transformsToRemove
      });
    },
    onPlay() {
      this.highlightPreview = false;
      this.$emit("play");
    }
  },
  computed: {
    hasSelections() {
      return !!this.selectionsData.length;
    },
    addClasses() {
      if (this.primary === 'add') {
        return '';
      }
      return '';
    }
  },
  watch:{
    noTransformsAdded(nv,ov) {
      if (!nv && ov) {
        this.highlightPreview = true;
      }
    },
    showClonePopup(nv) {
      this.$ana.track({
        eventCategory: 'ui',
        eventAction: 'showClonePopup',
        extra:{nv}
      });
    },
    showMenu(nv) {
      this.$ana.track({
        eventCategory: 'ui',
        eventAction: 'showMenu',
        extra:{nv}
      });
    }
  }
}
</script>
